import http from '@/config/http-common';
import { AxiosResponse } from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface SettingModel {
  categoryId: number;
  categoryDesc: string;
  description: string;
  settingId: number;
  shortDesc: string;
  tableId: number;
  value: string;
  valueTypeId: number;
  variantId: number;
  required: boolean;
}

export interface CategoryValues {
  categoryId: number;
  categoryDesc: string;
  variantId: number;
}

export interface SettingValueModel {
  boolValue: boolean | null;
  intValue: number | null;
  stringValue: String | null;
  intList: number[] | null;
  stringList: string[] | null;
  stringMap: Map<string, string> | null;
}

export const useSettingsStore = defineStore('settings', () => {
  const isLoading = ref(false);
  const error = ref<Error | unknown>(null);
  const categoryValues = ref([] as CategoryValues[]);
  const settingValue = ref<SettingValueModel | null>(null);

  const getSettings = async (settingCategoryId?: number) => {
    isLoading.value = true;
    const technicalSettings = ref([] as SettingModel[]);
    try {
      error.value = null;
      let url = '/setting';
      if (settingCategoryId) {
        url = `/setting?setting-category-id=${settingCategoryId}`;
      }
      const response: AxiosResponse<SettingModel[]> = await http.get(url);
      technicalSettings.value = response.data;
    } catch (err) {
      error.value = err;
    } finally {
      isLoading.value = false;
    }
    return technicalSettings;
  };

  const getSetting = async (settingId: number) => {
    isLoading.value = true;
    try {
      error.value = null;
      const response = await http.get(`/setting/${settingId}`);
      settingValue.value = response.data as SettingValueModel;
    } catch (err) {
      error.value = err;
    } finally {
      isLoading.value = false;
    }
    return settingValue;
  };

  const saveSettings = async (settingsToSave: SettingModel) => {
    try {
      error.value = null;
      await http.put(`/setting`, settingsToSave);
    } catch (err) {
      error.value = err;
    }
  };

  const getCategoryValues = async () => {
    isLoading.value = true;
    try {
      error.value = null;
      const response = await http.get('/setting/category');
      categoryValues.value = response.data as CategoryValues[];
    } catch (err) {
      error.value = err;
    } finally {
      isLoading.value = false;
    }
    return categoryValues;
  };

  return {
    getSettings,
    getSetting,
    saveSettings,
    getCategoryValues,
    isLoading,
    error,
  };
});
